import { AzureTicketCreatorConfig } from '@evinced-private/ui-common';

import { DEFAULT_AZURE_SCOPE_NAMES, toAzureScopeUri } from 'src/common/consts/azure-consts';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';

import { Logger } from './Logger';

/**
 * Parse space-separated Azure DevOps OAuth scope names (e.g. `vso.profile vso.work_write`).
 * Returns undefined when there are no non-empty names, so callers can fall back to the next source.
 */
const parseAzureScopeNames = (value?: string): string[] | undefined => {
	const names = value
		?.split(/\s+/)
		.map((name) => name.trim())
		.filter(Boolean);
	return names?.length ? names : undefined;
};

/**
 * Build Platform's config for the (otherwise host-agnostic) azure-ticket-creator module from
 * `@evinced-private/ui-common`. This is the one place that reads the Vite env and adapts the app's
 * logger onto the module's contract.
 *
 * Scope names resolve per tenant: `scopesFromConfig` (central config `AZURE_SCOPES`) wins, then the
 * `VITE_AZURE_SCOPES` build-time env var, then {@link DEFAULT_AZURE_SCOPE_NAMES}. Each bare name is
 * qualified with the fixed Azure DevOps resource id ({@link toAzureScopeUri}). Consumers should
 * memoize the result so the object stays referentially stable and the module's provider resolves it
 * once.
 */
export const buildAzureTicketCreatorConfig = (
	scopesFromConfig?: string
): AzureTicketCreatorConfig => {
	const scopeNames =
		parseAzureScopeNames(scopesFromConfig) ??
		parseAzureScopeNames(import.meta.env.VITE_AZURE_SCOPES as string | undefined) ??
		DEFAULT_AZURE_SCOPE_NAMES;

	return {
		clientId: import.meta.env.VITE_AZURE_APP_CLIENT_ID as string,
		redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URL as string,
		// Base URLs are optional — the module falls back to its Azure DevOps defaults when unset.
		apiBaseUrl: import.meta.env.VITE_AZURE_API_URL as string,
		usersBaseUrl: import.meta.env.VITE_AZURE_USERS_API_URL as string,
		// Scope names: per-tenant central config wins, then the env var, then the defaults.
		scopes: scopeNames.map(toAzureScopeUri),
		logger: Logger,
		appElement: PLATFORM_APP_ID
	};
};
